import { px } from "@/components/tool-kit/charts/marks";
import {
  type CategoryLabelLayout,
  CHART_FONT_SIZE,
  LINE_HEIGHT,
  type Rect,
  TICK_GAP,
  TICK_SIZE,
} from "@/lib/tool-kit/charts/layout";
import type { Scale } from "@/lib/tool-kit/charts/scale";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * Axes for charts drawn with `cartesianFrame`.
 *
 * A value axis draws its ticks, labels and (optionally) gridlines across the plot; a
 * category axis draws the names under a row of bars or beside a column of them. Number
 * formatting is passed in, so the same axis writes 1,234.5 in English and 1.234,5 in
 * German without the kit knowing about locales.
 *
 * APA mode draws dark axis lines and labels and no gridlines — APA 7 asks figures to
 * avoid gridlines unless the reader needs them to read values.
 */

const TICK_FONT = CHART_FONT_SIZE.tick;
/** Shift that centres a line of text on a y coordinate without `dominant-baseline`. */
const CENTER_SHIFT = TICK_FONT * 0.35;

function axisInk(apa: boolean): { line: string; label: string } {
  return apa
    ? { line: CHART_INK.text, label: CHART_INK.text }
    : { line: CHART_INK.axis, label: CHART_INK.muted };
}

export function ValueAxis({
  orient,
  plot,
  scale,
  ticks,
  format,
  grid = true,
  apa = false,
  zeroLine = false,
}: {
  /** `left`: values run up the side (column charts); `bottom`: along the foot. */
  orient: "left" | "bottom";
  plot: Rect;
  scale: Scale;
  ticks: readonly number[];
  format: (value: number) => string;
  grid?: boolean;
  apa?: boolean;
  /** Draw a solid line at zero — the baseline bars grow from. */
  zeroLine?: boolean;
}) {
  const ink = axisInk(apa);
  const showGrid = grid && !apa;
  const right = plot.x + plot.width;
  const bottom = plot.y + plot.height;
  const zero = scale(0);
  if (orient === "left") {
    const zeroInside = zeroLine && zero >= plot.y - 0.5 && zero <= bottom + 0.5;
    return (
      <g>
        {ticks.map((tick) => {
          const y = px(scale(tick));
          return (
            <g key={tick}>
              {showGrid ? (
                <line
                  stroke={CHART_INK.grid}
                  strokeWidth={1}
                  x1={px(plot.x)}
                  x2={px(right)}
                  y1={y}
                  y2={y}
                />
              ) : null}
              <line
                stroke={ink.line}
                strokeWidth={1}
                x1={px(plot.x - TICK_SIZE)}
                x2={px(plot.x)}
                y1={y}
                y2={y}
              />
              <text
                fill={ink.label}
                fontSize={TICK_FONT}
                textAnchor="end"
                x={px(plot.x - TICK_SIZE - TICK_GAP)}
                y={px(y + CENTER_SHIFT)}
              >
                {format(tick)}
              </text>
            </g>
          );
        })}
        <line
          stroke={ink.line}
          strokeWidth={1}
          x1={px(plot.x)}
          x2={px(plot.x)}
          y1={px(plot.y)}
          y2={px(bottom)}
        />
        {zeroInside ? (
          <line
            stroke={CHART_INK.text}
            strokeWidth={1}
            x1={px(plot.x)}
            x2={px(right)}
            y1={px(zero)}
            y2={px(zero)}
          />
        ) : null}
      </g>
    );
  }
  const zeroInside = zeroLine && zero >= plot.x - 0.5 && zero <= right + 0.5;
  return (
    <g>
      {ticks.map((tick) => {
        const x = px(scale(tick));
        return (
          <g key={tick}>
            {showGrid ? (
              <line
                stroke={CHART_INK.grid}
                strokeWidth={1}
                x1={x}
                x2={x}
                y1={px(plot.y)}
                y2={px(bottom)}
              />
            ) : null}
            <line
              stroke={ink.line}
              strokeWidth={1}
              x1={x}
              x2={x}
              y1={px(bottom)}
              y2={px(bottom + TICK_SIZE)}
            />
            <text
              fill={ink.label}
              fontSize={TICK_FONT}
              textAnchor="middle"
              x={x}
              y={px(bottom + TICK_SIZE + TICK_GAP + TICK_FONT * 0.8)}
            >
              {format(tick)}
            </text>
          </g>
        );
      })}
      <line
        stroke={ink.line}
        strokeWidth={1}
        x1={px(plot.x)}
        x2={px(right)}
        y1={px(bottom)}
        y2={px(bottom)}
      />
      {zeroInside ? (
        <line
          stroke={CHART_INK.text}
          strokeWidth={1}
          x1={px(zero)}
          x2={px(zero)}
          y1={px(plot.y)}
          y2={px(bottom)}
        />
      ) : null}
    </g>
  );
}

/**
 * Category names beside a row (`bottom`) or column (`left`) of slots, centred on
 * `positions`. Bottom labels come from `layoutCategoryLabels` (wrapped level, or
 * rotated −45° when they do not fit); left labels are pre-wrapped lines.
 */
export function CategoryAxis({
  orient,
  plot,
  positions,
  labels,
  apa = false,
  axisLine = false,
  ticks = false,
}: {
  orient: "bottom" | "left";
  plot: Rect;
  positions: readonly number[];
  labels: CategoryLabelLayout;
  apa?: boolean;
  /** Draw the axis line along the plot edge (when no value baseline sits there). */
  axisLine?: boolean;
  /** Draw a short tick at each position. */
  ticks?: boolean;
}) {
  const ink = axisInk(apa);
  const bottom = plot.y + plot.height;
  const lineHeight = TICK_FONT * LINE_HEIGHT;
  if (orient === "left") {
    return (
      <g>
        {positions.map((y, index) => {
          const lines = labels.lines[index] ?? [];
          const first =
            y - ((lines.length - 1) * lineHeight) / 2 + CENTER_SHIFT;
          return (
            // Positions are the categories in display order.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional categories
            <g key={index}>
              {ticks ? (
                <line
                  stroke={ink.line}
                  strokeWidth={1}
                  x1={px(plot.x - TICK_SIZE)}
                  x2={px(plot.x)}
                  y1={px(y)}
                  y2={px(y)}
                />
              ) : null}
              {lines.map((line, row) => (
                <text
                  fill={CHART_INK.text}
                  fontSize={TICK_FONT}
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional lines
                  key={row}
                  textAnchor="end"
                  x={px(plot.x - TICK_SIZE - TICK_GAP)}
                  y={px(first + row * lineHeight)}
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
        {axisLine ? (
          <line
            stroke={ink.line}
            strokeWidth={1}
            x1={px(plot.x)}
            x2={px(plot.x)}
            y1={px(plot.y)}
            y2={px(bottom)}
          />
        ) : null}
      </g>
    );
  }
  const top = bottom + TICK_SIZE + TICK_GAP;
  return (
    <g>
      {positions.map((x, index) => {
        const lines = labels.lines[index] ?? [];
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional categories
          <g key={index}>
            {ticks ? (
              <line
                stroke={ink.line}
                strokeWidth={1}
                x1={px(x)}
                x2={px(x)}
                y1={px(bottom)}
                y2={px(bottom + TICK_SIZE)}
              />
            ) : null}
            {labels.rotated ? (
              <text
                fill={CHART_INK.text}
                fontSize={TICK_FONT}
                textAnchor="end"
                transform={`rotate(-45 ${px(x)} ${px(top)})`}
                x={px(x)}
                y={px(top + TICK_FONT * 0.35)}
              >
                {lines[0]}
              </text>
            ) : (
              lines.map((line, row) => (
                <text
                  fill={CHART_INK.text}
                  fontSize={TICK_FONT}
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional lines
                  key={row}
                  textAnchor="middle"
                  x={px(x)}
                  y={px(top + TICK_FONT * 0.8 + row * lineHeight)}
                >
                  {line}
                </text>
              ))
            )}
          </g>
        );
      })}
      {axisLine ? (
        <line
          stroke={ink.line}
          strokeWidth={1}
          x1={px(plot.x)}
          x2={px(plot.x + plot.width)}
          y1={px(bottom)}
          y2={px(bottom)}
        />
      ) : null}
    </g>
  );
}
